import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, User as UserIcon, RefreshCw } from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [photoLimit, setPhotoLimit] = useState<number>(10000);
  const [perUploadLimit, setPerUploadLimit] = useState<number>(250);
  const [piiConfig, setPiiConfig] = useState({
    detect_faces: true,
    detect_names: true,
    detect_emails: true,
    detect_phone_numbers: true,
    detect_license_plates: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);

  useEffect(() => {
    loadUserAndSettings();
  }, []);

  const loadUserAndSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }
      setUser(user);

      // Get user's organization
      const { data: userOrg } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!userOrg) return;
      setOrganizationId(userOrg.organization_id);

      // Load organization settings
      const { data: org } = await supabase
        .from('organizations')
        .select('photo_limit, per_upload_limit, pii_config')
        .eq('id', userOrg.organization_id)
        .single();

      if (org) {
        setPhotoLimit(org.photo_limit || 10000);
        setPerUploadLimit(org.per_upload_limit || 250);
        if (org.pii_config && typeof org.pii_config === 'object') {
          const config = org.pii_config as any;
          setPiiConfig({
            detect_faces: config.detect_faces ?? true,
            detect_names: config.detect_names ?? true,
            detect_emails: config.detect_emails ?? true,
            detect_phone_numbers: config.detect_phone_numbers ?? true,
            detect_license_plates: config.detect_license_plates ?? true,
          });
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!organizationId) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          photo_limit: photoLimit,
          per_upload_limit: perUploadLimit,
          pii_config: piiConfig,
        })
        .eq('id', organizationId);

      if (error) throw error;
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReprocessPII = async () => {
    if (!organizationId) return;

    setIsReprocessing(true);
    try {
      // Call edge function to reprocess all photos with new PII config
      const { error } = await supabase.functions.invoke('reprocess-pii', {
        body: { organizationId, piiConfig },
      });

      if (error) throw error;
      toast.success('Started reprocessing photos for PII detection');
    } catch (error) {
      console.error('Error reprocessing PII:', error);
      toast.error('Failed to start PII reprocessing');
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Failed to sign out');
    } else {
      toast.success('Signed out successfully');
      navigate('/auth');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Settings</h1>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <UserIcon className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-6">
          {/* Photo Limit Setting */}
          <Card>
            <CardHeader>
              <CardTitle>Photo Limit</CardTitle>
              <CardDescription>
                Set the maximum number of photos allowed in your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label htmlFor="photo-limit" className="min-w-[120px]">
                  Total Storage Limit:
                </Label>
                <Input
                  id="photo-limit"
                  type="number"
                  min="1"
                  max="100000"
                  value={photoLimit}
                  onChange={(e) => setPhotoLimit(parseInt(e.target.value) || 10000)}
                  className="max-w-[200px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Per-Upload Limit Setting */}
          <Card>
            <CardHeader>
              <CardTitle>Per-Upload Limit</CardTitle>
              <CardDescription>
                Set the maximum number of photos that can be uploaded at once
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label htmlFor="per-upload-limit" className="min-w-[120px]">
                  Photos per Upload:
                </Label>
                <Input
                  id="per-upload-limit"
                  type="number"
                  min="1"
                  max="1000"
                  value={perUploadLimit}
                  onChange={(e) => setPerUploadLimit(parseInt(e.target.value) || 250)}
                  className="max-w-[200px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* PII Detection Settings */}
          <Card>
            <CardHeader>
              <CardTitle>PII Detection Settings</CardTitle>
              <CardDescription>
                Configure what types of personally identifiable information (PII) to detect in your photos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detect-faces"
                      checked={piiConfig.detect_faces}
                      onCheckedChange={(checked) =>
                        setPiiConfig({ ...piiConfig, detect_faces: !!checked })
                      }
                    />
                    <Label htmlFor="detect-faces" className="cursor-pointer">
                      Detect Faces
                    </Label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detect-names"
                      checked={piiConfig.detect_names}
                      onCheckedChange={(checked) =>
                        setPiiConfig({ ...piiConfig, detect_names: !!checked })
                      }
                    />
                    <Label htmlFor="detect-names" className="cursor-pointer">
                      Detect Names
                    </Label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detect-emails"
                      checked={piiConfig.detect_emails}
                      onCheckedChange={(checked) =>
                        setPiiConfig({ ...piiConfig, detect_emails: !!checked })
                      }
                    />
                    <Label htmlFor="detect-emails" className="cursor-pointer">
                      Detect Email Addresses
                    </Label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detect-phones"
                      checked={piiConfig.detect_phone_numbers}
                      onCheckedChange={(checked) =>
                        setPiiConfig({ ...piiConfig, detect_phone_numbers: !!checked })
                      }
                    />
                    <Label htmlFor="detect-phones" className="cursor-pointer">
                      Detect Phone Numbers
                    </Label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detect-plates"
                      checked={piiConfig.detect_license_plates}
                      onCheckedChange={(checked) =>
                        setPiiConfig({ ...piiConfig, detect_license_plates: !!checked })
                      }
                    />
                    <Label htmlFor="detect-plates" className="cursor-pointer">
                      Detect License Plates
                    </Label>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  onClick={handleReprocessPII}
                  disabled={isReprocessing}
                  variant="outline"
                  className="w-full"
                >
                  {isReprocessing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Reprocessing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reprocess All Photos for PII
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  This will reanalyze all photos using the current PII detection settings
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              size="lg"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
